import { debounce, show } from "../common";
import FilterBase from "./filter-base";
import FilterBox from "./filter-box";
import { FilterActiveValue } from "./active-filters";
import FilterParamStats from "./filter-stats";
import $ from "jquery";
import 'jquery-ui-pack';

export default class RangeFilter extends FilterBase {

    private sliderElement: HTMLElement;

    private minInputElement: HTMLInputElement;
    private maxInputElement: HTMLInputElement;
    private displayValueCoefficient = 1;
    private name: string;
    private unit: string;

    private defaultMinValue: number;
    private defaultMaxValue: number;

    private defaultMinElement: HTMLElement;
    private defaultMaxElement: HTMLElement;

    private rangeSeparator = '--';

    constructor(element: HTMLElement, filterBox: FilterBox) {
        super(element, filterBox);
        this.loadDisplayValueCoefficient();
        this.name = this.baseElement.dataset.paramName;
        this.sliderElement = this.baseElement.querySelector('[data-component-part="slider"]');
        const unitElement = this.baseElement.querySelector('[data-component-part="unit"]') as HTMLElement;
        if(unitElement) {
            this.unit = unitElement.innerText;
        }

        this.defaultMinElement = this.baseElement.querySelector('[data-component-part="default-min-label"]');
        this.defaultMaxElement = this.baseElement.querySelector('[data-component-part="default-max-label"]');
        this.defaultMinValue = Number(this.sliderElement.getAttribute('data-min-value')?.replace(',', '.'));
        this.defaultMaxValue = Number(this.sliderElement.getAttribute('data-max-value')?.replace(',', '.'));

        this.minInputElement = this.baseElement.querySelector('input.sliderValue[data-index="0"]') as HTMLInputElement;
        this.maxInputElement = this.baseElement.querySelector('input.sliderValue[data-index="1"]') as HTMLInputElement;

    }

    init(): void {
        this.initSlider();
        this.addChangeHandlers();
    }

    private loadDisplayValueCoefficient() {
        if (this.baseElement.dataset.displayValueCoefficient) {
            const coefficient = this.parseMoneyValue(this.baseElement.dataset.displayValueCoefficient);
            if (coefficient) this.displayValueCoefficient = coefficient;
        }
    }

    private initSlider(): void {
        const that = this;

        (<any>$(this.sliderElement)).slider({
            range: true,
            min: this.defaultMinValue,
            max: this.defaultMaxValue,
            step: this.calculateSliderStep(),
            values: [this.defaultMinValue, this.defaultMaxValue],
            slide: debounce(function () {
                const values: number[] = (<any>$(this)).slider('values');

                that.setMinimumValue(values[0]);
                that.setMaximumValue(values[1]);

                that.updateAttributeAndApplyChanges();
            }, 100, false)
        });
    }

    private addChangeHandlers() {
        const that = this;
        this.minInputElement.addEventListener('change', () =>
        {
            that.updateMinimumValue();
            that.updateAttributeAndApplyChanges();
        });
        this.maxInputElement.addEventListener('change', () => {
            that.updateMaximumValue();
            that.updateAttributeAndApplyChanges();
        });
    }

    public setMinimumValue(value: number) {
        let newValue = (value && value >= this.defaultMinValue) ? value : this.defaultMinValue;
        if(newValue > this.getMaximumValue()){
            const maxValue = this.getMaximumValue();
            this.setMaximumValue(newValue);
            newValue = maxValue;
        }
        this.setValue(0, newValue);
        this.minInputElement.value = newValue.toString();
    }

    public getMinimumValue(): number {
        return Number(this.minInputElement.value);
    }

    public setMaximumValue(value: number) {
        let newValue = (value && value >= this.defaultMinValue) ? value : this.defaultMaxValue;
        if(newValue < this.getMinimumValue()){
            const minValue = this.getMinimumValue();
            this.setMinimumValue(newValue);
            newValue = minValue;
        }
        this.setValue(1, newValue);
        this.maxInputElement.value = newValue.toString();
    }

    public getMaximumValue(): number {
        return Number(this.maxInputElement.value);
    }

    protected setValuesInternal(values: string): Promise<void> {
        var valuesToSelect = values.split(this.rangeSeparator);

        if (valuesToSelect.length > 0) {
            const minValue = this.convertToDisplayCurrency(this.parseMoneyValue(valuesToSelect[0]));
            if (minValue) {
                this.setMinimumValue(minValue);
            }
        }
        if (valuesToSelect.length > 1) {
            const maxValue = this.convertToDisplayCurrency(this.parseMoneyValue(valuesToSelect[1]));
            if (maxValue) {
                this.setMaximumValue(maxValue);
            }
        }

        return Promise.resolve();
    }

    public unselect(value: string){
        this.setMinimumValue(this.defaultMinValue);
        this.setMaximumValue(this.defaultMaxValue);

        this.updateAttributeAndApplyChanges();
    }

    public setStats(stats: FilterParamStats): void {
        if(!(stats.Min && stats.Max)){
            return;
        }
        show(this.baseElement);

        const isDefaultMin = this.getMinimumValue() === this.defaultMinValue;
        const isDefaultMax = this.getMaximumValue() === this.defaultMaxValue;
        
        this.setDefaultMinimumValue(this.convertToDisplayCurrency(stats.Min));
        this.setDefaultMaximumValue(this.convertToDisplayCurrency(stats.Max));


        (<any>$(this.sliderElement)).slider( "option", "min", this.defaultMinValue );
        (<any>$(this.sliderElement)).slider( "option", "max", this.defaultMaxValue );
        (<any>$(this.sliderElement)).slider( "option", "step", this.calculateSliderStep() );
        (<any>$(this.sliderElement)).slider( "values", [ this.getMinimumValue(), this.getMaximumValue() ] );

        if(isDefaultMin){
            this.setMinimumValue(this.defaultMinValue);
        }
        if(isDefaultMax){
            this.setMaximumValue(this.defaultMaxValue);
        }
    }

    private calculateSliderStep(): number {
        return this.round((this.defaultMaxValue - this.defaultMinValue) / 20);
    }

    private convertToDisplayCurrency(moneyAmount: number): number {
        const value = moneyAmount * this.displayValueCoefficient;
        return this.round(value);
    }

    private round(value: number): number{
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }

    protected clearValuesInternal(): void {
        this.setMinimumValue(this.defaultMinValue);
        this.setMaximumValue(this.defaultMaxValue);
    }

    private updateMinimumValue(): void {
        this.setMinimumValue(this.getMinimumValue());
    }

    private updateMaximumValue(): void {
        this.setMaximumValue(this.getMaximumValue());
    }

    private setDefaultMinimumValue(value: number): void {
        this.defaultMinValue = value;
        this.defaultMinElement.innerHTML = value.toString();
    } 
    
    private setDefaultMaximumValue(value: number): void {
        this.defaultMaxValue = value;
        this.defaultMaxElement.innerHTML = value.toString();
    }

    private setValue(valueIndex: number, value: number) {
        (<any>$(this.sliderElement)).slider('values', valueIndex, value);
    }
    
    protected getSelectedValuesConcat(): string {
        const min = this.getMinimumValue();
        const max = this.getMaximumValue();
        if(min === this.defaultMinValue && max === this.defaultMaxValue) {
            return null;
        }

        return `${this.convert(min)}${this.rangeSeparator}${this.convert(max)}`;
    }

    protected getActiveFilterValues(): FilterActiveValue[] {

        const min = this.getMinimumValue();
        const max = this.getMaximumValue();

        if(min === this.defaultMinValue && max === this.defaultMaxValue){
            return [];
        }

        return [<FilterActiveValue> { value: `${this.paramId}=${min}-${max}`, displayName: `${this.name}: ${min} - ${max} ${this.unit}` }];
    }

    private convert(value: number): number {
        return value / this.displayValueCoefficient;
    }

    private parseMoneyValue(stringValue: string): number {
        if (!stringValue)
            return 0;

        const floatValue = parseFloat(stringValue.replace(',', '.'));
        if (floatValue)
            return floatValue;

        return 0;
    }
}
